import { useNavigation } from '@react-navigation/native';
import { Headline1, HorizontalList, theme } from 'aimo-ui';
import LoginStates from 'components/account/LoginStates';
import SubPromotionCard from 'components/account/subscription/SubPromotionCard';
import BGView from 'components/common/BGView';
import LabeledHorizontalCardsList from 'components/common/LabeledHorizontalCardsList';
import MenuItem from 'components/common/MenuItem';
import Icon from 'components/icons/Icon';
import CardItem from 'components/services/CardItem';
import ChipCardItem from 'components/services/ChipCardItem';
import CircleImageWrapper from 'components/services/CircleImageWrapper';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import ServiceContext from 'context/ServiceContext';
import useAfterAuthenticationRedirect from 'hooks/useAfterAuthenticationRedirect';
import useBottomMenuHeight from 'hooks/useBottomMenuHeight';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import useReadPurchasedPrepaidTickets from 'hooks/useReadPurchasedPrepaidTickets';
import GroupBy from 'lodash/groupBy';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MyPermitCard from 'screens/permit/MyPermitCard';
import { useHslStore } from 'stores/hslStore';
import { useCityParkingStore } from 'stores/cityParkingStore';
import styled from 'styled-components/native';
import {
  ScreenContainer,
  ScreenWrapper,
  StyledIllustrationImage,
  StyledImage,
  StyledWrapper,
} from 'styles/ContainerStyles';
import {
  CountryCode,
  HslTicketContent,
  Maybe,
  PredefinedTicket,
} from 'types/generatedSchemaTypes';
import {
  AccountScreenNavigationProp,
  RootNavProps,
  ServicesNavProps,
} from 'types/navigation';
import { isAndroid, isWeb } from 'utils/deviceUtils';
import {
  parseFeatureFlagObject,
  showFeatureFlag,
  TypeDictionary,
} from 'utils/featureFlagUtils';
import { titleTypeText, validTimeText } from 'utils/hslUtils';
import { sortPermitsByWaitingAction } from 'utils/permitUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import CustomChips from './CustomChips';
import ServiceImage from './ServiceImage';

const PurchasedServicesWrapper = styled.View`
  padding-horizontal: 20px;
`;

const ImageWrapperForOnboardingV2 = styled.View`
  justify-content: flex-end;
  flex: 1;
  transform: translate(0, 40px);
  z-index: 999;
  margin-top: ${isAndroid ? '-20px' : '10px'};
`;

const contentContainerStyle = { flexGrow: 1 };

const PermitImage: React.FC<{ testID?: string }> = ({ testID }) => (
  <StyledImage
    source={require('assets/images/permit.png')}
    width={128}
    height={110}
    center
    testID={testID}
  />
);

const TurkuCityImage = ({ testID }: { testID?: string }) => (
  <StyledImage
    source={require('assets/images/turku.png')}
    center
    width={150}
    height={80}
    testID={testID}
  />
);

const HSLImage = ({ testID }: { testID?: string }) => (
  <StyledImage
    source={require('assets/images/hsl.png')}
    center
    width={130}
    height={50}
    testID={testID}
  />
);
const HslTicketImage = ({ testID }: { testID?: string }) => (
  <Icon
    name={'HSL-Outlined'}
    color={theme.colors.white}
    size="m"
    testID={testID}
  />
);

const permitImage = () => <PermitImage testID="permit-card-image" />;

const turkuCityImage = () => <TurkuCityImage testID="turku-permit-image" />;
const hslImage = () => <HSLImage testID="hsl-ticket-image" />;

const Content = () => {
  const { t } = useTranslation();
  const {
    flags: { ride, permit_turku, hsl_ticket, sales_order_permits },
  } = useContext(FeatureFlagsContext);
  const showRide = showFeatureFlag(ride);
  const cityPermitsTurku = showFeatureFlag(permit_turku);
  const hslTickets = showFeatureFlag(hsl_ticket);
  const { navigate } = useNavigation<RootNavProps>();
  const { navigate: serviceNavigate } = useNavigation<ServicesNavProps>();
  const { navigateToAccountLogin } = useAfterAuthenticationRedirect();
  const { isLoggedIn } = useIsLoggedIn();
  const countryObject = parseFeatureFlagObject<
    TypeDictionary<CountryCode, boolean>
  >(sales_order_permits, 'sales_order_permits');
  const permitCountries = Object.keys(countryObject)
    .filter((key) => countryObject[key as CountryCode])
    .map((key) => key as CountryCode);

  return (
    <StyledWrapper mb={20} center={isWeb} maxWidth={isWeb ? 600 : undefined}>
      <>
        <StyledWrapper
          mb={20}
          center={isWeb}
          maxWidth={isWeb ? 600 : undefined}>
          <Headline1 {...testIdentifiers('services-subtitle')}>
            {t('services.availableTitle')}
          </Headline1>
        </StyledWrapper>
        <SubPromotionCard />
        <CardItem
          title={t('services.permitTitle')}
          text={t('services.permitText')}
          isPopular={true}
          imageContainer={permitImage}
          onPress={() => navigate('longTermParking', { screen: 'mapScreen' })}
          testID="permit-card"
          countryToShow={permitCountries}
        />
        {showRide && (
          <CardItem
            title={t('services.bookRideTitle')}
            text={t('services.bookRideText')}
            onPress={() => navigate('mainTabs', { screen: 'rideScreen' })}
            testID="book-ride-card"
            countryToShow={['FI']}
          />
        )}
        {cityPermitsTurku && (
          <CardItem
            bgColor={theme.colors.lightBg.blue}
            textColor={theme.colors.white}
            title={t('services.turkuPermitsTitle')}
            text={t('services.turkuPermitsText')}
            imageContainer={turkuCityImage}
            onPress={() => {
              useCityParkingStore.getState().resetState();
              isLoggedIn
                ? navigate('cityParking', { screen: 'cityPmcs' })
                : navigateToAccountLogin();
            }}
            testID="city-permit-card"
            countryToShow={['FI']}
          />
        )}
        {hslTickets && !isWeb && (
          <CardItem
            bgColor={theme.colors.lightBg.hslBlue}
            textColor={theme.colors.white}
            title={t('services.parkAndRideTicketTitle')}
            text={t('services.parkAndRideTicketText')}
            imageContainer={hslImage}
            onPress={() =>
              serviceNavigate('hslNavigator', {
                screen: 'parkAndRideTutorial',
              })
            }
            testID="hsl-ticket-card"
            countryToShow={['FI']}
          />
        )}
        <CustomChips />
      </>
    </StyledWrapper>
  );
};

const ServicesContent = () => {
  if (isWeb) {
    return <Content />;
  }

  return (
    <BGView noMargins>
      <Content />
    </BGView>
  );
};

const CircleWrappedServiceImage = () => {
  const ImageComponent = useCallback(
    () => <ServiceImage turkuPmc={false} testID={'service-image'} />,
    []
  );
  return (
    <CircleImageWrapper
      backgroundColor={theme.colors.lightBg.green}
      testIdentifiersPrefix={'service-image-'}
      ImageComponent={ImageComponent}
    />
  );
};

const MyPrepaidCard = (item: Maybe<PredefinedTicket>) => {
  const { navigate } = useNavigation<AccountScreenNavigationProp>();
  return (
    <ChipCardItem
      title={item?.parkZoneName || ''}
      subTitle={item?.name || ''}
      LeftComponent={CircleWrappedServiceImage}
      onPress={() =>
        navigate('servicesScreen', {
          screen: 'prepaidTicket',
          params: { ticketId: item?.sessionId as string },
        })
      }
      testID="my-prepaid-chip-card"
    />
  );
};

export const CircleWrappedHslTicketImage = () => {
  const ImageComponent = useCallback(
    () => <HslTicketImage testID="hsl-ticket-image" />,
    []
  );
  return (
    <CircleImageWrapper
      backgroundColor={theme.colors.lightBg.hslBlue}
      ImageComponent={ImageComponent}
      testIdentifiersPrefix={'hsl-ticket-image-'}
    />
  );
};
const MyHslTicketCard = (item: HslTicketContent) => {
  const { navigate } = useNavigation<ServicesNavProps>();
  return (
    <ChipCardItem
      title={titleTypeText(item.hslDetails.ticketType)}
      subTitle={validTimeText(item.hslDetails) ?? ''}
      LeftComponent={CircleWrappedHslTicketImage}
      onPress={() => {
        useHslStore.getState().setSelectedHslTicket({
          ticketId: item.hslDetails.ticketId,
          ticketType: item.hslDetails.ticketType,
        });
        navigate('hslNavigator', { screen: 'hslTicketRenderView' });
      }}
      testID="my-hsl-ticket-chip-card"
    />
  );
};

const MyPrepaidSessionsContents = ({
  prepaidSessions,
}: {
  prepaidSessions?: PredefinedTicket[];
}) => {
  const { t } = useTranslation();
  const { navigate } = useNavigation<ServicesNavProps>();
  return (
    <>
      <MenuItem
        testID="services-my-prepaids-title"
        menuLabel={t('services.myPrepaids')}
        color={theme.colors.black}
        borderColor={theme.colors.gray100}
        noBorder
        withArrow
        arrowSize="s"
        bold
        onPress={() => navigate('prepaidTickets')}
      />
      {prepaidSessions && prepaidSessions.length > 0 && (
        <HorizontalList
          listData={prepaidSessions}
          listElement={({ item }) => MyPrepaidCard(item)}
          withBackground={theme.colors.white}
          {...testIdentifiers('my-prepaids-list')}
        />
      )}
    </>
  );
};

const MyHslTicketsContent = ({
  hslTickets,
}: {
  hslTickets: HslTicketContent[];
}) => {
  const { t } = useTranslation();
  const { navigate } = useNavigation<ServicesNavProps>();
  return (
    <>
      <MenuItem
        testID="services-my-hsl-tickets-title"
        menuLabel={t('services.myHslTickets')}
        onPress={() => navigate('hslNavigator', { screen: 'hslTicket' })}
        color={theme.colors.black}
        borderColor={theme.colors.gray100}
        noBorder
        withArrow
        arrowSize="s"
        bold
      />
      {hslTickets?.length && (
        <HorizontalList
          listData={hslTickets}
          listElement={({ item }) => MyHslTicketCard(item)}
          withBackground={theme.colors.white}
          testID="my-hsl-tickets-list"
        />
      )}
    </>
  );
};

const MyPrepaidSessions = () => {
  const { isLoggedIn } = useIsLoggedIn();
  const { purchasedPrepaidTickets } = useReadPurchasedPrepaidTickets({
    fetchAll: true,
  });

  if (purchasedPrepaidTickets.length === 0) {
    return <></>;
  }

  return (
    <StyledWrapper center={isWeb} maxWidth={isWeb ? 600 : undefined}>
      {isLoggedIn && (
        <MyPrepaidSessionsContents
          prepaidSessions={purchasedPrepaidTickets as PredefinedTicket[]}
        />
      )}
    </StyledWrapper>
  );
};

const MyHslTickets = () => {
  const { isLoggedIn } = useIsLoggedIn();
  const { myHslTickets } = useContext(ServiceContext);

  if (myHslTickets?.length === 0) {
    return <></>;
  }

  return (
    <StyledWrapper center={isWeb} maxWidth={isWeb ? 600 : undefined}>
      {isLoggedIn && <MyHslTicketsContent hslTickets={myHslTickets} />}
    </StyledWrapper>
  );
};

const ServiceContent = () => {
  const tabBarHeight = useBottomMenuHeight();
  const { t } = useTranslation();
  const {
    customer: { permits, benefits },
  } = useContext(AccountContext);
  const { navigate } = useNavigation<RootNavProps>();
  const [groupedPermits, setGroupedPermits] = useState(
    GroupBy(permits, 'permitType')
  );

  useEffect(() => {
    setGroupedPermits(GroupBy(permits, 'permitType'));
  }, [permits]);

  return (
    <ScreenWrapper
      contentContainerStyle={contentContainerStyle}
      padding={0}
      tabBarHeight={tabBarHeight}>
      <PurchasedServicesWrapper>
        <LabeledHorizontalCardsList
          listData={sortPermitsByWaitingAction([
            ...(groupedPermits.WAITING_LIST ?? []),
            ...(groupedPermits.ACCESS ?? []),
            ...(groupedPermits.SALES_ORDER ?? []),
            ...(groupedPermits.ACCESS_TO_MEMBER_GROUP_LTP ?? []),
            ...(groupedPermits.DIGITAL_PERMIT ?? []),
          ])}
          cardComponent={MyPermitCard}
          label={t('services.myPermits')}
          labelOnPress={() =>
            navigate('permits', {
              permitType: [
                'ACCESS',
                'SALES_ORDER',
                'ACCESS_TO_MEMBER_GROUP_LTP',
                'DIGITAL_PERMIT',
                'WAITING_LIST',
              ],
            })
          }
          testIdentifiersPrefix="permits"
          benefits={benefits ?? []}
        />
        <MyPrepaidSessions />
        {!isWeb && <MyHslTickets />}
        <LabeledHorizontalCardsList
          listData={groupedPermits.LOYALTY}
          label={t('services.loyaltyPermits')}
          labelOnPress={() => navigate('permits', { permitType: 'LOYALTY' })}
          cardComponent={MyPermitCard}
          testIdentifiersPrefix="loyalty-permits"
        />
      </PurchasedServicesWrapper>
      <ServicesContent />
    </ScreenWrapper>
  );
};

const ServicesDefault: React.FC = () => {
  const { t } = useTranslation();
  const { isLoggedIn } = useIsLoggedIn();
  const tabBarHeight = useBottomMenuHeight();

  const renderServiceScreenInWeb = () => {
    return (
      <>
        <StyledImage
          testID="services-main-image"
          width={309}
          height={300}
          center
          source={require('assets/images/illustration-service.png')}
        />
        <LoginStates
          fixedHeader={true}
          loginText={t('services.notSignedInDescription')}
        />
      </>
    );
  };

  const renderServiceScreenInNative = () => {
    return (
      <>
        <ImageWrapperForOnboardingV2>
          <StyledIllustrationImage
            testID="services-main-image"
            center
            resizeMode="contain"
            source={require('assets/images/illustration-service.png')}
          />
        </ImageWrapperForOnboardingV2>

        <LoginStates
          fixedHeader={false}
          loginText={t('services.notSISUDescription')}
        />
      </>
    );
  };

  const renderer = () =>
    isWeb ? (
      <ScreenWrapper tabBarHeight={tabBarHeight}>
        {isLoggedIn ? <ServiceContent /> : renderServiceScreenInWeb()}
      </ScreenWrapper>
    ) : (
      <ScreenWrapper
        contentContainerStyle={contentContainerStyle}
        padding={0}
        tabBarHeight={tabBarHeight}>
        {isLoggedIn ? <ServiceContent /> : renderServiceScreenInNative()}
      </ScreenWrapper>
    );

  return (
    <ScreenContainer
      mt={isWeb ? 170 : 0}
      background={isWeb ? theme.colors.white : theme.colors.gray100}>
      {renderer()}
    </ScreenContainer>
  );
};

export default ServicesDefault;
